import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { exponentOf } from './currencies';
import { i18n } from './i18n';
import { formatMinor, toMinor } from './money';
import type { TagRef } from './tracking';
import { useOrgId } from './org';
import { lineAmounts, type AmountsAre } from './sales';

export type PurchaseKind = 'bill' | 'expense' | 'debit_note' | 'purchase_order';

export interface PurchaseLine {
  lineNo: number;
  itemId: string | null;
  itemName: string | null;
  sku: string | null;
  description: string | null;
  quantity: string;
  unitPrice: string;
  accountId: string;
  accountName: string;
  taxCodeId: string;
  taxCode: string;
  taxRate: string;
  taxKind: string;
  reportsOnReturn: boolean;
  net: string;
  tax: string;
  gross: string;
  selfAssessedTax: string;
  recoverablePercent: string;
  homeNet: string;
  homeTax: string;
  /** Class and project of the line (migration 1700). */
  classId?: string | null;
  className?: string | null;
  projectId?: string | null;
  projectName?: string | null;
  /** Purchase order lines: the line's id, received on bills, closed by hand (migration 1711). */
  lineId?: string;
  receivedQuantity?: string;
  closed?: boolean;
  /** Bill lines: the purchase order line received. */
  purchaseOrderLineId?: string | null;
  purchaseOrderId?: string | null;
  purchaseOrderNumber?: string | null;
}

export interface PurchaseAllocation {
  id: string;
  date: string;
  amount: string;
  type: 'payment' | 'debit_note' | 'bill' | 'journal_entry';
  refId: string;
  refNumber: string;
  /** Realised exchange gain (positive) or loss in home currency, if any. */
  exchangeGainLoss: string | null;
}

export interface PurchaseDocument {
  /** Class, location, project and tags (migration 1700). */
  classId?: string | null;
  className?: string | null;
  locationId?: string | null;
  locationName?: string | null;
  projectId?: string | null;
  projectName?: string | null;
  tags?: TagRef[];
  id: string;
  kind: PurchaseKind;
  number: string | null;
  status: string;
  overdue: boolean;
  /** null: an expense without a payee. */
  contactId: string | null;
  contactName: string | null;
  contactTrn: string | null;
  contactCountry: string | null;
  billingAddress: Record<string, string | null> | null;
  issueDate: string;
  dueDate: string | null;
  termsDays: number | null;
  supplyDate: string | null;
  amountsAre: AmountsAre;
  currency: string;
  fxRate: string;
  homeCurrency: string;
  subtotal: string;
  tax: string;
  total: string;
  balance: string;
  homeSubtotal: string;
  homeTax: string;
  homeTotal: string;
  homeSelfAssessedTax: string;
  selfAssessedTax: string;
  recoverableTax: string;
  taxSummary: Array<{
    taxCode: string;
    rate: string;
    net: string;
    tax: string;
    selfAssessedTax: string;
    homeTax: string;
    homeSelfAssessedTax: string;
  }>;
  reference: string | null;
  memo: string | null;
  creditReason: string | null;
  paymentAccountId: string | null;
  paymentAccountName: string | null;
  paymentMethod: string | null;
  relatedDocumentId: string | null;
  relatedDocumentNumber: string | null;
  journalEntryId: string | null;
  voidEntryId: string | null;
  voidReason: string | null;
  version: number;
  createdAt: string;
  approvedAt: string | null;
  approvedByName: string | null;
  /** Open item brought over from QuickBooks at the conversion date: no lines, read-only. */
  opening: boolean;
  lines: PurchaseLine[];
  allocations: PurchaseAllocation[];
  warnings: string[];
}

export interface PurchaseRow {
  id: string;
  type: PurchaseKind | 'vendor_payment';
  number: string | null;
  reference: string | null;
  date: string;
  dueDate: string | null;
  /** null: an expense without a payee. */
  contactId: string | null;
  contactName: string | null;
  category: string | null;
  memo: string | null;
  total: string;
  balance: string;
  currency: string;
  homeTotal: string;
  homeBalance: string;
  status: string;
  overdue: boolean;
}

type Bucket = { amount: string; count: number };
export interface PurchaseSummary {
  overdue: Bucket;
  open: Bucket;
  notDueYet: Bucket;
  paidLast30: Bucket;
  drafts: Bucket;
  unappliedCredits: Bucket;
  expensesLast365: Bucket;
}

export interface OpenBills {
  /** Open bills and journal entry charges on A/P (kind journal_entry). */
  bills: Array<{
    kind: 'bill' | 'journal_entry';
    id: string;
    number: string;
    /** The journal entry of a journal_entry row. */
    entryId: string | null;
    reference: string | null;
    contactId: string;
    contactName: string;
    issueDate: string;
    dueDate: string | null;
    total: string;
    balance: string;
    overdue: boolean;
    currency: string;
    fxRate: string;
    homeBalance: string;
  }>;
  credits: Array<{
    kind: 'debit_note' | 'payment' | 'journal_entry';
    id: string;
    number: string;
    entryId: string | null;
    contactId: string;
    date: string;
    total: string;
    balance: string;
    currency: string;
  }>;
  homeCurrency: string;
}

export interface VendorPayment {
  id: string;
  number: string;
  status: 'posted' | 'void';
  contactId: string;
  contactName: string;
  date: string;
  paymentAccountId: string;
  paymentAccountName: string;
  method: string | null;
  reference: string | null;
  memo: string | null;
  amount: string;
  unapplied: string;
  currency: string;
  fxRate: string;
  homeCurrency: string;
  homeAmount: string;
  journalEntryId: string;
  voidEntryId: string | null;
  voidReason: string | null;
  allocations: PurchaseAllocation[];
}

export const PURCHASE_INFO: Record<
  PurchaseKind,
  { label: string; path: string; listPath: string; numberLabel: string; dateLabel: string }
> = {
  bill: {
    label: 'Bill',
    path: '/expenses/bills',
    listPath: '/expenses/bills',
    numberLabel: 'Bill no.',
    dateLabel: 'Bill date',
  },
  expense: {
    label: 'Expense',
    path: '/expenses/expenses',
    listPath: '/expenses',
    numberLabel: 'Ref no.',
    dateLabel: 'Payment date',
  },
  debit_note: {
    label: 'Debit note',
    path: '/expenses/debit-notes',
    listPath: '/expenses',
    numberLabel: 'Debit note no.',
    dateLabel: 'Debit note date',
  },
  purchase_order: {
    label: 'Purchase order',
    path: '/expenses/purchase-orders',
    listPath: '/expenses/purchase-orders',
    numberLabel: 'PO no.',
    dateLabel: 'Purchase order date',
  },
};

const KIND_KEY = {
  bill: 'bill',
  expense: 'expense',
  debit_note: 'debitNote',
  purchase_order: 'purchaseOrder',
} as const;

/** The catalogue key of a purchase kind's messages (purchasePages.kinds.<key>.…). */
export const purchaseKindKey = (kind: PurchaseKind) => KIND_KEY[kind];

/** PURCHASE_INFO's labels in the reader's language (the English map stays as it is). */
export const purchaseLabel = (kind: PurchaseKind): string =>
  i18n.t(`purchasePages.kinds.${KIND_KEY[kind]}.label`);
export const purchaseNumberLabel = (kind: PurchaseKind): string =>
  i18n.t(`purchasePages.kinds.${KIND_KEY[kind]}.numberLabel`);
export const purchaseDateLabel = (kind: PurchaseKind): string =>
  i18n.t(`purchasePages.kinds.${KIND_KEY[kind]}.dateLabel`);

/** A purchases row's type ("Bill", "Bill payment") in the reader's language. */
export const purchaseTypeLabel = (type: PurchaseRow['type']): string =>
  type === 'vendor_payment' ? i18n.t('purchasePages.kinds.billPayment.label') : purchaseLabel(type);

/** Where a purchases row opens. */
export function purchaseRowPath(r: Pick<PurchaseRow, 'type' | 'id'>): string {
  return r.type === 'vendor_payment'
    ? `/expenses/payments/${r.id}`
    : `${PURCHASE_INFO[r.type].path}/${r.id}`;
}

export function usePurchaseDocument(id: string | undefined) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['purchases', orgId, 'document', id],
    queryFn: () => api<PurchaseDocument>(`/orgs/${orgId}/purchases/documents/${id}`),
    enabled: Boolean(orgId && id),
  });
}

export function usePurchaseDocuments(kind?: PurchaseKind, contactId?: string) {
  const orgId = useOrgId();
  const qs = new URLSearchParams();
  if (kind) qs.set('kind', kind);
  if (contactId) qs.set('contactId', contactId);
  return useQuery({
    queryKey: ['purchases', orgId, 'documents', kind ?? 'all', contactId ?? 'all'],
    queryFn: () => api<PurchaseDocument[]>(`/orgs/${orgId}/purchases/documents?${qs.toString()}`),
    enabled: Boolean(orgId),
  });
}

export function usePurchaseTransactions(contactId?: string) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['purchases', orgId, 'transactions', contactId ?? 'all'],
    queryFn: () =>
      api<PurchaseRow[]>(
        `/orgs/${orgId}/purchases/transactions${contactId ? `?contactId=${contactId}` : ''}`,
      ),
    enabled: Boolean(orgId),
  });
}

export function usePurchaseSummary(contactId?: string) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['purchases', orgId, 'summary', contactId ?? 'all'],
    queryFn: () =>
      api<PurchaseSummary>(
        `/orgs/${orgId}/purchases/summary${contactId ? `?contactId=${contactId}` : ''}`,
      ),
    enabled: Boolean(orgId),
  });
}

export function useOpenBills(contactId?: string | null) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['purchases', orgId, 'open-bills', contactId ?? 'all'],
    queryFn: () =>
      api<OpenBills>(
        `/orgs/${orgId}/purchases/open-bills${contactId ? `?contactId=${contactId}` : ''}`,
      ),
    enabled: Boolean(orgId),
  });
}

export function useVendorPayment(id: string | undefined) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['purchases', orgId, 'payment', id],
    queryFn: () => api<VendorPayment>(`/orgs/${orgId}/purchases/payments/${id}`),
    enabled: Boolean(orgId && id),
  });
}

/** How a VAT code behaves on a purchase line. */
export interface PurchaseCode {
  rate: string;
  kind: string;
  returnBox: string | null;
}

/** Reverse charge and imports: the buyer accounts for the VAT; the vendor charges none. */
export const isSelfAssessed = (c: PurchaseCode | undefined): boolean =>
  Boolean(c && (c.kind === 'reverse_charge' || c.returnBox === '6'));

/** Only standard-rated VAT charged by the vendor can be blocked or partly recovered. */
export const canLimitRecovery = (c: PurchaseCode | undefined): boolean =>
  Boolean(c && c.kind === 'standard' && c.returnBox !== null && c.returnBox !== '6');

const divRound = (n: bigint, d: bigint): bigint => {
  const q = n / d;
  const r = n % d;
  return (r < 0n ? -r : r) * 2n >= d ? (n < 0n ? q - 1n : q + 1n) : q;
};

/**
 * One purchase line, mirroring ledger-core computePurchaseLine (bigint, no floats): charged VAT
 * as on a sales line; self-assessed VAT is net × rate and is not owed to the vendor.
 */
export function purchaseLineAmounts(
  quantity: string,
  unitPriceMinor: bigint,
  code: PurchaseCode | undefined,
  amountsAre: AmountsAre,
): { net: bigint; tax: bigint; gross: bigint; selfAssessed: bigint } {
  const bp = code ? Math.round(Number(code.rate) * 100) : 0;
  if (!isSelfAssessed(code)) {
    const a = lineAmounts(quantity, unitPriceMinor, bp, amountsAre);
    return { ...a, selfAssessed: 0n };
  }
  const a = lineAmounts(quantity, unitPriceMinor, 0, 'exclusive');
  const sa = amountsAre === 'out_of_scope' ? 0n : divRound(a.net * BigInt(bp), 10000n);
  return { net: a.net, tax: 0n, gross: a.net, selfAssessed: sa };
}

/** Recoverable VAT of a charged line at a recovery percentage (mirrors splitRecoverable). */
export function recoverableTax(
  net: bigint,
  tax: bigint,
  rateBp: number,
  recoverablePercent: number,
): bigint {
  if (recoverablePercent >= 100 || tax === 0n) return tax;
  if (recoverablePercent <= 0) return 0n;
  const bp = BigInt(Math.round(recoverablePercent * 100));
  const netR = divRound(net * bp, 10000n);
  const taxR = divRound(netR * BigInt(rateBp), 10000n);
  return taxR > tax ? tax : taxR;
}

type OpenBill = OpenBills['bills'][number];

/**
 * Pay bills (E5) before saving, as the server checks it (vendor-payments.service): per bill, the
 * payment plus credit applied is no more than its open balance and the bill is not dated after
 * the payment; per vendor, the credit applied is no more than the vendor has. Messages name the
 * bill or vendor; amounts are bigint minor units of each bill's currency.
 */
export function payBillsProblems(
  chosen: Array<{ id: string; amount: string; credit: string }>,
  open: Pick<OpenBills, 'bills' | 'credits'>,
  date: string,
): { problems: string[]; overpaid: Set<string> } {
  const problems: string[] = [];
  const overpaid = new Set<string>();
  const bills = new Map<string, OpenBill>(open.bills.map((b) => [b.id, b]));
  const creditUsed = new Map<string, { name: string; ccy: string; minor: bigint }>();
  let any = false;
  for (const p of chosen) {
    const b = bills.get(p.id);
    if (!b) continue;
    const exp = exponentOf(b.currency);
    const money = toMinor(p.amount, exp) ?? 0n;
    const credit = toMinor(p.credit, exp) ?? 0n;
    const balance = toMinor(b.balance, exp) ?? 0n;
    if (money > 0n || credit > 0n) any = true;
    if (money + credit > balance) {
      problems.push(
        i18n.t('purchasePages.checks.overBalance', {
          number: b.number,
          currency: b.currency,
          balance: formatMinor(balance, exp),
        }),
      );
      overpaid.add(b.id);
    }
    if (money > 0n && b.issueDate > date)
      problems.push(i18n.t('purchasePages.checks.datedAfterPayment', { number: b.number }));
    if (credit > 0n) {
      const v = creditUsed.get(b.contactId) ?? { name: b.contactName, ccy: b.currency, minor: 0n };
      v.minor += credit;
      creditUsed.set(b.contactId, v);
    }
  }
  for (const [contactId, v] of creditUsed) {
    const exp = exponentOf(v.ccy);
    const available = open.credits
      // debit notes and journal credits dated after the payment are not available yet (server rule)
      .filter((c) => c.contactId === contactId && (c.kind === 'payment' || c.date <= date))
      .reduce((s, c) => s + (toMinor(c.balance, exponentOf(c.currency)) ?? 0n), 0n);
    if (v.minor > available)
      problems.push(
        i18n.t('purchasePages.checks.vendorCredit', {
          name: v.name,
          currency: v.ccy,
          available: formatMinor(available, exp),
          applied: formatMinor(v.minor, exp),
        }),
      );
  }
  if (chosen.length && !any) problems.push(i18n.t('purchasePages.checks.enterAmount'));
  return { problems, overpaid };
}

/** Apply a debit note (E6): no more than the credit left in all, nor than each bill's balance. */
export function debitNoteApplyProblems(
  amounts: Record<string, string>,
  bills: Array<Pick<OpenBill, 'id' | 'number' | 'balance'>>,
  available: bigint,
  exp: number,
): { problems: string[]; over: Set<string>; total: bigint } {
  const problems: string[] = [];
  const over = new Set<string>();
  let total = 0n;
  for (const b of bills) {
    const minor = toMinor(amounts[b.id] ?? '', exp) ?? 0n;
    total += minor;
    const balance = toMinor(b.balance, exp) ?? 0n;
    if (minor > balance) {
      over.add(b.id);
      problems.push(
        i18n.t('purchasePages.checks.overBillBalance', {
          number: b.number,
          balance: formatMinor(balance, exp),
        }),
      );
    }
  }
  if (total > available)
    problems.push(
      i18n.t('purchasePages.checks.overCredit', {
        total: formatMinor(total, exp),
        available: formatMinor(available, exp),
      }),
    );
  return { problems, over, total };
}
